// src/redux/slices/homeSlice.ts
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import type { PayloadAction } from "@reduxjs/toolkit"
import type { Difficulty, LanguageCode, Stroke, WordsResponse } from "../../utils/types"
import type { RootState } from "../../app/store"
import { toast } from "sonner"
import { baseUrl, nonAlphabetLanguages } from "@/lib/utils"

type HomeState = {
  gameState: "loading" | "ready" | "running" | "finished"
  foreignLanguage: LanguageCode
  nativeLanguage: LanguageCode
  foreignWords: string[]
  romanizedWords: string[]
  nativeWords: string[]
  inputWords: string[]
  nativeInputWords: string[]
  wordIndex: number
  nativeWordIndex: number
  strokeTimeline: number[]
  foreignStrokeTimeline: Stroke[]
  strictMode: boolean
  mixedMode: boolean
  difficulty: Difficulty
  filterUnderscores: boolean
  nativeWordActive: boolean
  overflowedLines: number[]
  startTime: number
  pauseTime: number | undefined
  pauseSum: number
  ellapsedTime: number // TODO: second time for native words
  timeMode: 1 | 15 | 30 | 60
  loadingState: "idle" | "pending" | "failed"
}

const arr = ["learning", "through", "muscle", "memory"]

const initialState: HomeState = {
  gameState: "ready",
  foreignLanguage: (localStorage.getItem("foreignLanguage") as LanguageCode | null) ?? "es",
  nativeLanguage: (localStorage.getItem("nativeLanguage") ?? "en") as LanguageCode,
  //   foreignWords: Array(100).fill('nativefingers') as string[],
  foreignWords: Array.from({ length: 100 }, (_, index) => arr[index % arr.length]),
  romanizedWords: Array(100).fill("test") as string[],
  nativeWords: Array(100).fill("nativefingers") as string[],
  inputWords: [],
  nativeInputWords: [],
  wordIndex: 0,
  nativeWordIndex: 0,
  strokeTimeline: [],
  foreignStrokeTimeline: [],
  strictMode: localStorage.getItem("strictMode") === "true",
  mixedMode: localStorage.getItem("mixedMode") === "true",
  difficulty: (localStorage.getItem("difficulty") ?? "beginner") as Difficulty,
  filterUnderscores: true,
  nativeWordActive: false,
  overflowedLines: [],
  ellapsedTime: 0,
  startTime: 0,
  pauseTime: undefined,
  pauseSum: 0,
  timeMode: Number(localStorage.getItem("timeMode") ?? 15) as 15 | 30 | 60,
  loadingState: "idle",
}

export const getWordsAsync = createAsyncThunk("game/getWordsAsync", async (_, thunkAPI) => {
  const state = thunkAPI.getState() as RootState

  try {
    const params = new URLSearchParams({
      foreignLanguage: state.home.foreignLanguage,
      nativeLanguage: state.home.nativeLanguage,
      difficulty: state.home.difficulty,
      filterUnderscores: state.home.filterUnderscores.toString(),
    })

    const res = await fetch(`${baseUrl}/words?${params}`)

    if (!res.ok) {
      toast.error("Could not fetch words from server")
      throw new Error("Request failed")
    }

    const data = (await res.json()) as WordsResponse

    // TODO: fetch user locale and preferred foreign language
    toast.success("words fetched successfully")

    const returnValue: WordsResponse = {
      foreignWords: data.foreignWords,
      nativeWords: data.nativeWords,
      romanizedWords: data.romanizedWords,
    }

    if (nonAlphabetLanguages.includes(state.home.foreignLanguage) && !state.home.strictMode) {
      // swap foreignWordsa nd romanizedWords if strict mode is off
      const temp = returnValue.foreignWords
      returnValue.foreignWords = returnValue.romanizedWords
      returnValue.romanizedWords = temp
    }

    return returnValue
  } catch (error) {
    console.error("could not get words from server: ", error)
    toast.error("Could not fetch words from server")
    throw new Error("could not get words from server")
  }
})

const homeSlice = createSlice({
  name: "home",
  initialState,
  reducers: {
    startHome: (state, action: PayloadAction<number>) => {
      console.log("game started")
      state.gameState = "running"
      state.startTime = action.payload
    },
    endHome: state => {
      state.gameState = "finished"
    },
    resetHome: state => {
      state.gameState = "ready"
      state.inputWords = []
      state.nativeInputWords = []
      state.wordIndex = 0
      state.nativeWordIndex = 0
      state.overflowedLines = []
      state.strokeTimeline = []
      state.foreignStrokeTimeline = []
      state.nativeWordActive = false
      state.ellapsedTime = 0
      state.startTime = 0
      state.pauseTime = undefined
      state.pauseSum = 0
    },
    setForeignLanguage: (state, action: PayloadAction<LanguageCode>) => {
      state.foreignLanguage = action.payload
      localStorage.setItem("foreignLanguage", action.payload)
    },
    setNativeLanguage: (state, action: PayloadAction<LanguageCode>) => {
      state.nativeLanguage = action.payload
      localStorage.setItem("nativeLanguage", action.payload)
    },
    setTimeMode: (state, action: PayloadAction<15 | 30 | 60>) => {
      state.timeMode = action.payload
      localStorage.setItem("timeMode", action.payload.toString())
    },
    setDifficulty: (state, action: PayloadAction<Difficulty>) => {
      state.difficulty = action.payload
      localStorage.setItem("difficulty", action.payload)
    },
    toggleFilterUnderscores: state => {
      state.filterUnderscores = !state.filterUnderscores
      localStorage.setItem("filterUnderscores", state.filterUnderscores.toString())
    },
    assessGameState: (state, action: PayloadAction<number>) => {
      if (state.gameState !== "running") return

      const currentTime = action.payload
      const currentPause = state.pauseTime ? currentTime - state.pauseTime : 0
      const diff = (currentTime - state.startTime - state.pauseSum - currentPause) / 1000
      state.ellapsedTime = diff // 	TODO: maybe add second ellapsedTime array for easier chart calculation at the end

      if (diff > state.timeMode) state.gameState = "finished"
    },
    addScore: (state, action: PayloadAction<string>) => {
      state.foreignWords.push(action.payload)
    },
    setInputWords: (state, action: PayloadAction<{ input: string; time: number }>) => {
      const inputWords = action.payload.input.split(/\s+/) // all white spaces
      //TODO: maybe refactor, string as parameter and deal with all array logic here.
      if (state.mixedMode) {
        state.inputWords = inputWords.filter((_, index) => index % 2 === 0)
        state.nativeInputWords = inputWords.filter((_, index) => index % 2 !== 0)
        state.wordIndex = state.inputWords.length - 1
        state.nativeWordIndex = state.nativeInputWords.length - 1
        state.nativeWordActive = inputWords.length % 2 === 0
        // pause timer if native word is active
        if (state.nativeWordActive && !state.pauseTime) {
          // going from foreign to native word
          state.pauseTime = action.payload.time
        } else if (!state.nativeWordActive && state.pauseTime) {
          // going from native to foreign word
          state.pauseSum += action.payload.time - state.pauseTime
          state.pauseTime = undefined
        }
      } else {
        state.inputWords = inputWords
        state.wordIndex = inputWords.length - 1
      }

      state.strokeTimeline.push(action.payload.time)

      if (!state.nativeWordActive) {
        const lastForeignChar = state.inputWords.join(" ").at(-1)
        if (lastForeignChar) state.foreignStrokeTimeline.push({ char: lastForeignChar, time: action.payload.time })
      }
      // deleting input deletes stroke time
      while (state.foreignStrokeTimeline.length > state.inputWords.join(" ").length) {
        state.foreignStrokeTimeline.pop()
      }

      //   console.log('inputWords', state.inputWords.slice())
      //   console.log('nativeInputWords', state.nativeInputWords.slice())
      //   console.log("wordIndex", state.wordIndex)
      //   console.log("nativeWordIndex", state.nativeWordIndex)
      //   console.log('foreignWords', state.foreignWords.slice())
    },
    pushPrestripIndex: (state, action: PayloadAction<number>) => {
      // make sure doesn't get triggered twice for foreign and native word
      if (state.mixedMode && !state.nativeWordActive) return

      state.overflowedLines.push(action.payload)
    },
    popPrestripIndex: state => {
      if (state.overflowedLines.length === 0) return
      state.overflowedLines.pop()
    },
    toggleStrictMode: state => {
      state.strictMode = !state.strictMode
      if (nonAlphabetLanguages.includes(state.foreignLanguage)) {
        const temp = state.foreignWords
        state.foreignWords = state.romanizedWords
        state.romanizedWords = temp
      }
      localStorage.setItem("strictMode", state.strictMode.toString())
    },
    toggleMixedMode: state => {
      state.mixedMode = !state.mixedMode
      localStorage.setItem("mixedMode", state.mixedMode.toString())
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getWordsAsync.pending, state => {
        state.loadingState = "pending"
      })
      .addCase(getWordsAsync.fulfilled, (state, action) => {
        state.foreignWords = action.payload.foreignWords
        state.nativeWords = action.payload.nativeWords
        state.romanizedWords = action.payload.romanizedWords

        state.loadingState = "idle"
      })
      .addCase(getWordsAsync.rejected, (state, action) => {
        console.error("Error fetching words: ", action.error.message)
        state.loadingState = "failed"
      })
  },
})

export const {
  startHome,
  endHome,
  resetHome,
  setForeignLanguage,
  setNativeLanguage,
  setTimeMode,
  setDifficulty,
  toggleFilterUnderscores,
  assessGameState,
  addScore,
  setInputWords,
  pushPrestripIndex,
  popPrestripIndex,
  toggleStrictMode,
  toggleMixedMode,
} = homeSlice.actions
export default homeSlice.reducer
